import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { signIn } from 'aws-amplify/auth';
import * as PATH from 'constants/paths';
import { FormikHelpers } from 'formik';

import { useOnline } from 'hooks/useOnline';
import usePromiseStatus from 'hooks/usePromiseStatus';

import { AuthModal, CognitoExceptions, IModalProps } from 'interfaces/auth';

import { AlertComponent } from 'components/_basic/AlertComponent';
import { useAuthModalsContext } from 'components/AuthModals';
import { MODAL_ERRORS } from 'components/AuthModals/types';
import { EnterConfirmCodeWarning } from 'components/Registration/RegistrationForms/EnterConfirmCodeWarning/EnterConfirmCodeWarning';

import { insertId } from 'helpers/path';
import ToastService from 'helpers/ToastService';

import { LoginFormView } from './components/LoginFormView/LoginFormView';
import { IFormData } from './types';

import styles from './Login.module.scss';

const formInitialValues: IFormData = {
  email: '',
  password: '',
};

export const Login: FC<IModalProps> = ({ isModalOpened, onModalClose }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const pSignUp = searchParams.get('pSignUp');

  const navigate = useNavigate();

  const uuid = searchParams.get('uuid');

  const [responseError, setResponseError] = useState('');
  const [isSetUpPassword, setIsSetUpPassword] = useState(false);

  const { track, isPending } = usePromiseStatus();
  const { setAuthModal, setResetFormData, setModalError, modalError, changeSetUpData } = useAuthModalsContext();

  const nextStepHandler = () => {
    setAuthModal(AuthModal.ENTER_CODE);
  };

  const loginErrorsMap = new Map<string, string>([
    [CognitoExceptions.NotAuthorizedException, t('rightEmailAndPassword')],
    [CognitoExceptions.UserAlreadyAuthenticatedException, t('alreadySignedIn')],
    [CognitoExceptions.ExpiredCodeException, `${t('notConfirmedEmail')}${t('enterTheCode')}`],
  ]);

  const handleModalClose = () => {
    setResponseError('');
    onModalClose();
  };

  const isOnline = useOnline();

  const onSubmit = async ({ email, password }: IFormData, actions: FormikHelpers<IFormData>): Promise<void> => {
    setModalError(null);
    if (!pSignUp) {
      localStorage.removeItem('onlyPayedSignUp');
    }
    try {
      const { isSignedIn } = await track(signIn({ username: email, password }));
      if (isSignedIn) {
        setResponseError('');
        actions.resetForm();
        handleModalClose();

        navigate(uuid ? insertId(PATH.BUSINESS_TOOL, uuid) : PATH.WORKSPACE, { replace: true });
      }
      setResetFormData((prev) => ({ ...prev, email, password }));
    } catch (error) {
      if (!isOnline) {
        ToastService.showError(
          'The changes will be saved after the connection is restored',
          'Your connection is lost',
          {
            autoClose: 5000,
          },
        );
        return;
      }
      error instanceof Error && setResponseError(loginErrorsMap.get(error.name) ?? error.message);
      setModalError(MODAL_ERRORS.LOGIN_ERROR);
    }
  };

  const setUpPasswordHandler = (email: string): void => {
    changeSetUpData((prev) => ({ ...prev, email }));
    setAuthModal(AuthModal.SETUP_PASSWORD);
  };

  const forgetPasswordHandler = (email: string) => {
    setResetFormData((prev) => ({ ...prev, email }));
    setAuthModal(AuthModal.RECOVER);
  };

  const signUpClickHandler = () => setAuthModal(AuthModal.SIGN_UP);

  const renderAlert = () => {
    if (responseError) {
      return <AlertComponent severity="error" message={responseError} className={styles.alert} />;
    }
    if (modalError) {
      return <EnterConfirmCodeWarning nextStepHandler={nextStepHandler} />;
    }
    return null;
  };

  return (
    <LoginFormView
      formInitialValues={formInitialValues}
      isModalOpened={isModalOpened}
      isPending={isPending}
      isSetUpPassword={isSetUpPassword}
      onModalClose={handleModalClose}
      onSubmit={onSubmit}
      setIsSetUpPassword={setIsSetUpPassword}
      onForgetPasswordClick={forgetPasswordHandler}
      onSetUpPassword={setUpPasswordHandler}
      onSignUpClick={signUpClickHandler}
      alert={renderAlert()}
    />
  );
};
